import { useState } from "react";
import { FotoFCA, fotosTipos } from "@/mocks/fca";

interface Props {
  fotos: FotoFCA[];
}

export default function TreePhotoGallery({ fotos }: Props) {
  const [filtro, setFiltro] = useState("todas");
  const [selected, setSelected] = useState<FotoFCA | null>(null);

  const filtered = filtro === "todas" ? fotos : fotos.filter((f) => f.tipo === filtro);

  const formatDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-foreground-900 font-heading flex items-center gap-2">
          <i className="ri-camera-line text-primary-500"></i>
          Galeria de Fotos
        </h3>
        <button className="btn-primary text-xs">
          <i className="ri-add-line"></i>
          Adicionar fotos
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        {fotosTipos.map((t) => (
          <button
            key={t.value}
            onClick={() => setFiltro(t.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              filtro === t.value
                ? "bg-primary-500 text-white"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {t.label}
            {t.value !== "todas" && (
              <span className="ml-1 opacity-60">
                ({fotos.filter((f) => f.tipo === t.value).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Photo grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-foreground-500 text-sm">
          <i className="ri-image-line text-3xl block mb-2 opacity-30"></i>
          Nenhuma foto encontrada para este filtro
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((foto) => (
            <div
              key={foto.id}
              className="bg-white rounded-card border border-background-200/70 overflow-hidden group cursor-pointer hover:border-accent-300 transition-all duration-200"
              onClick={() => setSelected(foto)}
            >
              <div className="relative h-40 bg-background-100">
                <img
                  src={foto.url}
                  alt={foto.descricao}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                <span className="absolute top-2 right-2 badge bg-white/90 text-foreground-700 text-[10px]">
                  {foto.tipo}
                </span>
              </div>
              <div className="p-2.5">
                <p className="text-xs text-foreground-800 leading-tight line-clamp-2">{foto.descricao}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[10px] text-foreground-500">{formatDate(foto.data)}</span>
                  <span className="text-[10px] text-foreground-500">{foto.tecnico}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-card max-w-3xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={selected.url}
                alt={selected.descricao}
                className="w-full max-h-[60vh] object-contain bg-background-100"
              />
              <button
                onClick={() => setSelected(null)}
                className="absolute top-3 right-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors text-foreground-700"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-foreground-900 font-medium">{selected.descricao}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {formatDate(selected.data)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {selected.tecnico}
                </span>
                <span className={`badge bg-background-100 text-foreground-600`}>{selected.tipo}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}